import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  DialogContent,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import "dayjs/locale/ko";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchPhotoDiary, getCurrentDateYYYYMM } from "../Hooks/Api";
import UploadButtonDiary from "./UploadButtonDiary";
import { formatDateDialog, getThumbnailUrl } from "./Home";
import PhotoDialog from "./PhotoDialog";
import { useRecoilValue } from "recoil";
import { TokenAtom, TokenInfo } from "../Hooks/TokenAtom";
import MySnackbar from "../Hooks/MySnackBar";

import { createTheme, ThemeProvider } from "@mui/material/styles";

// 렌더링마다 재생성되지 않도록 컴포넌트 외부에 정의
const pickerThemeDiary = createTheme({
  palette: {
    primary: { main: "#8c6744" },
    secondary: { main: "#cf9e62" },
  },
  typography: {
    fontFamily: "LeeSeoyun, sans-serif",
  },
});
const Photodiary = () => {
  const [value, setValue] = useState(getCurrentDateYYYYMM());
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLatest, setIsLatest] = useState(true); // 최신순/과거순 상태
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [snackBarSeverity, setSnackBarSeverity] = useState("success"); // 메시지 유형 설정
  const [currentId, setCurrentId] = useState(null); // 현재 선택된 id 저장
  const [detailSearchOpen, setDetailSearchOpen] = useState(false);
  const queryClient = useQueryClient();
  const tokens = useRecoilValue(TokenAtom);
  const tokenInfo = useRecoilValue(TokenInfo);
  const specificDate = dayjs("1988-12-10"); // 특정 날짜를 생성
  const inputRef = useRef();

  const {
    data: itemData,
    isLoading,
    isError,
  } = useQuery(["photoDiarys", value], () => fetchPhotoDiary(value, tokens), {
    staleTime: 300000, // 캐시된 데이터의 유효 시간 설정 (예: 5분)
    cacheTime: 900000, // 캐시된 데이터를 유지하는 시간 설정 (예: 15분)
  });

  const mutation = useMutation((date) => fetchPhotoDiary(date, tokens), {
    onSuccess: (data) => {
      queryClient.setQueryData(["photoDiarys", value], data);
      queryClient.invalidateQueries(["photoDiarys", value]); // 쿼리 무효화
    },
  });

  useEffect(() => {
    if (currentId && itemData) {
      const updatedDiary = itemData.find(
        (photoBatch) => photoBatch.batch_number === currentId
      );

      if (updatedDiary) {
        setSelectedPhoto(updatedDiary);
      }
    }
  }, [itemData, currentId]); // itemData 또는 currentId가 변경될 때마다 실행

  const handleDetailClickOpen = () => {
    setDetailSearchOpen(true);
  };

  const handleDetailClose = () => {
    setDetailSearchOpen(false);
  };
  const handleDetailSearch = () => {
    const keyword = inputRef.current.value;
    setValue(keyword);
    mutation.mutate(keyword);
    handleDetailClose();
  };

  const handleResetClick = () => {
    const newDate = getCurrentDateYYYYMM();
    setValue(newDate);
    mutation.mutate(newDate);
  };
  const handleNewClick = () => {
    setValue(specificDate);
    mutation.mutate(specificDate);
  };
  const handleToggleClick = () => {
    setIsLatest((prevIsLatest) => !prevIsLatest); // 상태 토글
  };

  const handleUploadSuccess = (status) => {
    if (status === 201) {
      setSnackBarMessage("업로드 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photoDiarys", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 404) {
      setSnackBarMessage("날짜없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleDeleteSuccess = (status) => {
    if (status === 204) {
      setSnackBarMessage("삭제 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photoDiarys", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 404) {
      setSnackBarMessage("데이터없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleUpdateSuccess = (status) => {
    if (status === 200) {
      setSnackBarMessage("수정 성공");
      setSnackBarSeverity("success");
      setIsSnackBarOpen(true);
      queryClient.invalidateQueries(["photoDiarys", value]); // 쿼리 무효화
    } else if (status === 401) {
      setSnackBarMessage("권한없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else if (status === 404) {
      setSnackBarMessage("데이터없음");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    } else {
      setSnackBarMessage("서버오류");
      setSnackBarSeverity("error");
      setIsSnackBarOpen(true);
    }
  };

  const handleCloseSnackBar = () => {
    setIsSnackBarOpen(false);
  };

  if (isLoading) {
    return <div>로딩중...</div>;
  }

  if (isError) {
    return <div>로그인을 다시 해주세요</div>;
  }

  // Helper function to group photos by date and then by batch_number
  const groupPhotosByDateAndBatch = (photos) => {
    return photos.reduce((dateAcc, item) => {
      const batchGroups = item.photos.reduce((batchAcc, photo) => {
        (batchAcc[photo.batch_number] =
          batchAcc[photo.batch_number] || []).push(photo);
        return batchAcc;
      }, {});

      Object.keys(batchGroups).forEach((batchNumber) => {
        if (!dateAcc[item.photos[0].date]) dateAcc[item.photos[0].date] = [];
        dateAcc[item.photos[0].date].push({
          batchNumber,
          photos: batchGroups[batchNumber],
        });
      });

      return dateAcc;
    }, {});
  };

  // Group photos by date and batch_number
  const groupedPhotos = groupPhotosByDateAndBatch(itemData);
  const handlePhotoClick = (photoBatch) => {
    setCurrentId(photoBatch.photos[0].batch_number); // 클릭된 video의 id를 저장
    setSelectedPhoto(photoBatch);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCurrentId(null); // id 초기화
    setDialogOpen(false);
    setSelectedPhoto(null); // 다이얼로그 닫을 때 선택된 사진 초기화
  };
  return (
    <Box sx={{ width: "100%", height: "auto", pb: 3 }}>
      <Box
        sx={{
          width: "100%",
          height: "auto",
          mb: 1,
          mt: 0,
          mx: 0,
          px: 2,
          border: "1px solid #ddd",
          borderRadius: "8px",
          backgroundColor: "#f5f1e7",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // 그림자 추가
        }}
      >
        <Box sx={{ width: "100%", height: "auto", mb: 2, mt: 1 }}>
          <Grid container spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <ThemeProvider theme={pickerThemeDiary}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="ko"
                >
                  <DesktopDatePicker
                    sx={{ mt: 1, width: "100%" }}
                    margin="dense"
                    format="YYYY년"
                    label="캘린더"
                    value={
                      dayjs(value).isSame(specificDate, "day")
                        ? dayjs()
                        : dayjs(value)
                    }
                    onChange={(newValue) => setValue(newValue)}
                    views={["year"]}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: {
                          width: "100%",
                          height: "40px",
                          backgroundColor: "white", // ← 폼 배경 하얗게
                          borderRadius: "8px", // ← 둥근 모서리(선택)
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)", // ← 그림자
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },

                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },

                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#8c6744",
                          },
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                            color: "#8c6744",
                          },
                          "& .MuiFormLabel-root": {
                            color: "#8c6744",
                          },
                          "& .MuiFormLabel-root.Mui-focused": {
                            color: "#8c6744", // 예: 강조색
                          },
                          "& .MuiSvgIcon-root path": {
                            fill: "#8c6744",
                          },
                        },
                      },
                      actionBar: {
                        actions: ["today"],
                      },
                    }}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </Grid>
          </Grid>
          <Grid
            container
            spacing={1}
            justifyContent="center"
            sx={{ pt: "4px" }}
          >
            <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                onClick={handleDetailClickOpen}
                sx={{
                  width: "100%",
                  height: "40px",
                  background: "linear-gradient(45deg, #b78609, #edb56f)",
                  color: "white",
                  "&:hover": {
                    background: "linear-gradient(45deg, #a67855, #f8e5d0)",
                  },
                }} // 캘린더와 동일한 높이 설정
              >
                상세검색
              </Button>
            </Grid>
            <Grid item xs={3}>
              <Button
                variant="contained"
                onClick={handleNewClick}
                sx={{
                  width: "100%",
                  height: "40px", // 캘린더와 동일한 높이
                  background: "linear-gradient(45deg, #8e44ad, #e84393)",
                  color: "#fff",
                  borderRadius: 1,
                  "&:hover": {
                    background: "linear-gradient(45deg, #a64fcf, #ff5ea2)",
                  },
                }}
              >
                NEW
              </Button>
            </Grid>
            <Grid item xs={2}>
              <IconButton
                onClick={handleResetClick}
                sx={{
                  width: 40,
                  height: 40,
                  background: "linear-gradient(45deg, #b78609, #edb56f)",
                  color: "white",
                  borderRadius: "50%", // 동그랗게
                  "&:hover": {
                    background: "linear-gradient(45deg, #a67855, #f8e5d0)",
                  },
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Grid>

            <Grid item xs={3}>
              <Button
                variant="contained"
                onClick={handleToggleClick}
                sx={{
                  width: "100%",
                  height: "40px",
                  color: "white",
                  background: isLatest
                    ? "linear-gradient(45deg, #b78609, #edb56f)" // 과거순 배경
                    : "linear-gradient(45deg, #8e44ad, #e84393)", // 최신순 배경
                  "&:hover": {
                    background: isLatest
                      ? "linear-gradient(45deg, #b78609, #edb56f)" // 과거순 호버
                      : "linear-gradient(45deg, #8e44ad, #e84393)", // 최신순 호버
                  },
                }}
              >
                {isLatest ? "과거순" : "최신순"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box sx={{ mt: 0 }}>
        {(isLatest
          ? Object.keys(groupedPhotos).sort((a, b) => new Date(b) - new Date(a))
          : Object.keys(groupedPhotos).sort((a, b) => new Date(a) - new Date(b))
        ) // 날짜 순으로 정렬
          .map((date) => (
            <Card
              key={date}
              sx={{
                width: "100vw",
                ml: -1,
                mb: 1,
                px: 2,
                py: 1,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                backgroundColor: "#f5f4f2",
              }}
            >
              {/* 날짜 표시 */}
              <Typography variant="subtitle1" sx={{ mb: 1, color: "#8c6744" }}>
                {formatDateDialog(date)}
              </Typography>

              <Box
                sx={{
                  width: "100%",
                }}
              >
                {groupedPhotos[date].map(({ batchNumber, photos }) => (
                  <Card
                    key={batchNumber}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      cursor: "pointer",
                      backgroundColor: "#f5f4f2",
                    }}
                    onClick={() => handlePhotoClick({ date, photos })}
                  >
                    {/* 배치 제목 표시 */}
                    <Box
                      sx={{
                        width: "100%",
                        backgroundColor: "#B8860B",
                        padding: "4px",
                        borderRadius: "8px 8px 0 0",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{ color: "white", px: 1 }}
                      >
                        {photos[0].title}
                      </Typography>
                    </Box>

                    {/* 사진 썸네일 영역 */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        padding: "4px",
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {photos.slice(0, 3).map((photo, index) => (
                        <Box
                          key={photo.id}
                          sx={{
                            width: "100px",
                            height: "100px",
                            borderRadius: 2,
                            overflow: "hidden",
                            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            position: "relative",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        >
                          <img
                            src={getThumbnailUrl(photo.image)}
                            alt={photo.diary}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          {index === 0 && photos.length > 1 && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                backgroundColor: "rgba(0,0,0,0.6)",
                                color: "white",
                                padding: "2px 4px",
                                borderRadius: "0 4px 0 4px",
                                fontSize: "12px",
                              }}
                            >
                              +{photos.length}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                ))}
              </Box>
            </Card>
          ))}
      </Box>

      <Dialog
        open={detailSearchOpen}
        onClose={handleDetailClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "#f5f4f2", // 전체 배경 색
            borderRadius: "12px", // 모서리 둥글게
          },
        }}
      >
        <DialogTitle sx={{ color: "#8c6744", bgcolor: "#f5f4f2" }}>
          단어 검색
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: "#f5f4f2",
            pt: "16px !important",
          }}
        >
          <TextField
            label="검색어 입력"
            variant="outlined"
            fullWidth
            inputRef={inputRef}
            margin="dense"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f5f4f2",
                "& fieldset": {
                  borderColor: "#c9b08a",
                },
                "&:hover fieldset": {
                  borderColor: "#b89263",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#8c6744",
                  borderWidth: "2px",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#8c6744",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#8c6744",
              },
              /* ✨ 입력 텍스트 색상 변경 */
              "& .MuiInputBase-input": {
                color: "#8c6744", // ← 원하는 텍스트 색상
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDetailSearch}
            variant="contained"
            sx={{
              background: "#B8860B", // 민트 → 초록
              color: "white",
              fontWeight: "bold",
              "&:hover": {
                background: "#DAA520", // hover 버전
              },
            }}
          >
            검색
          </Button>
          <Button
            onClick={handleDetailClose}
            variant="outlined"
            sx={{
              color: "#B8860B",
              borderColor: "#B8860B", // ← outlined 라서 border도 맞춰줘야 함
              "&:hover": {
                borderColor: "#DAA520",
                background: "#DAA520",
              },
            }}
          >
            취소
          </Button>
        </DialogActions>
      </Dialog>
      {tokenInfo.is_superuser && (
        <UploadButtonDiary onUploadSuccess={handleUploadSuccess} />
      )}
      {selectedPhoto && (
        <PhotoDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          date={formatDateDialog(selectedPhoto.date)}
          photos={selectedPhoto.photos}
          onDeleteSuccess={handleDeleteSuccess}
          onUpdateSuccess={handleUpdateSuccess}
        />
      )}
      <MySnackbar
        open={isSnackBarOpen}
        onClose={handleCloseSnackBar}
        message={snackBarMessage}
        severity={snackBarSeverity}
      />
    </Box>
  );
};

export default Photodiary;
